<template>
  <div class="customer-detail-page">
    <div v-if="loading" class="loading-block">
      <van-loading type="spinner" />
      <div class="loading-text">正在加载客户资料...</div>
    </div>

    <template v-else-if="!bindingInfo?.linked">
      <div class="empty-state">
        <div class="empty-text">未关联系统档案</div>
        <div class="empty-text-tip">
          将该{{ sessionTypeText }}与系统中的{{ isGroupChat ? '学员' : '学员或线索' }}关联，快速查看档案、课表和跟进记录
        </div>
        <div class="action-group">
          <button type="button" class="primary-action-btn" @click="handleOpenSearchPopup">关联已有</button>
          <button type="button" class="secondary-action-btn" @click="handleAddNew">新建并关联</button>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="linked-content">
        <button v-if="!isGroupChat && currentViewTarget" type="button" class="header-more-btn more-btn-float" @click="showMoreSheet = true">
          <van-icon name="ellipsis" />
        </button>

        <div v-if="isGroupChat" class="tabs-row">
          <div v-if="studentTargets.length > 0" class="student-tabs-bar">
            <div v-for="stu in studentTargets" :key="stu.bindingId" class="student-tab-item"
              :class="{ active: activeStudentBindingId === stu.bindingId }" @click="switchStudent(stu.bindingId)">
              <span class="student-tab-name">{{ stu.name }}</span>
              <van-icon name="cross" class="student-tab-close" @click.stop="handleUnbind(stu)" />
            </div>
          </div>

          <button v-if="currentViewTarget" type="button" class="header-more-btn" @click="showMoreSheet = true">
            <van-icon name="ellipsis" />
          </button>
        </div>

        <template v-if="activeStudentTarget">
          <StudentLinkedPage :key="'stu-' + activeStudentTarget.targetId" :target="activeStudentTarget" />
        </template>
        <template v-else-if="activeViewType === 'consultation' && consultationTarget">
          <ConsultationLinkedPage :key="'con-' + consultationTarget.targetId" :target="consultationTarget" />
        </template>
      </div>
    </template>

    <BindingSearchPopup v-model:show="showSearchPopup" :session-type="sessionType" :session-id="sessionId"
      :initial-tab="searchInitialTab" @bound="handleBound" />

    <van-action-sheet v-model:show="showAddNewSheet" :actions="addNewActions" cancel-text="取消" @select="onAddNewSelect"
      round />

    <van-action-sheet v-model:show="showMoreSheet" :actions="moreActions" cancel-text="取消" @select="onMoreSelect"
      round />

    <StudentCreatePopup v-model:show="showStudentCreate" @submit="handleCreateStudent" />
    <ConsultationCreatePopup v-model:show="showConsultationCreate" @submit="handleCreateConsultation" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { showToast, showConfirmDialog, showLoadingToast, closeToast } from 'vant'
import { ensureWecomLogin } from '@/utils/wecomAuth'
import { registerWecomApp, getWecomContextInfo } from '@/utils/wecom'
import { getCurrentBinding, unbindBinding, createStudentBinding, createConsultationBinding } from '@/api/crm'
import BindingSearchPopup from './components/BindingSearchPopup.vue'
import StudentCreatePopup from './components/StudentCreatePopup.vue'
import ConsultationCreatePopup from './components/ConsultationCreatePopup.vue'
import StudentLinkedPage from './pages/StudentLinkedPage.vue'
import ConsultationLinkedPage from './pages/ConsultationLinkedPage.vue'

const route = useRoute()
const loading = ref(true)
const bindingInfo = ref(null)
const sessionType = ref('')
const sessionId = ref('')
const externalContactName = ref('')
const showSearchPopup = ref(false)
const searchInitialTab = ref('student')
const showAddNewSheet = ref(false)
const showMoreSheet = ref(false)
const showStudentCreate = ref(false)
const showConsultationCreate = ref(false)
const activeStudentBindingId = ref(null)
const activeViewType = ref('student')

const isSingleChat = computed(() => sessionType.value === 'single_chat' || sessionType.value === 'single_chat_tools')
const isGroupChat = computed(() => sessionType.value === 'group_chat' || sessionType.value === 'group_chat_tools')

const studentTargets = computed(() => bindingInfo.value?.studentTargets || [])
const consultationTarget = computed(() => bindingInfo.value?.consultationTarget || null)
const hasConsultation = computed(() => !!consultationTarget.value)
const hasStudents = computed(() => studentTargets.value.length > 0)

const sessionTypeText = computed(() => isGroupChat.value ? '群聊' : '客户')

const activeStudentTarget = computed(() => {
  if (!studentTargets.value.length) return null
  if (activeStudentBindingId.value) {
    return studentTargets.value.find(s => s.bindingId === activeStudentBindingId.value) || studentTargets.value[0]
  }
  return studentTargets.value[0]
})

const linkedSummaryText = computed(() => {
  const parts = []
  if (studentTargets.value.length) {
    parts.push(`${studentTargets.value.length}位学员`)
  }
  if (hasConsultation.value) {
    parts.push('1条线索')
  }
  return '已关联：' + parts.join('、')
})

const currentViewTarget = computed(() => {
  if (activeViewType.value === 'consultation' && consultationTarget.value) {
    return consultationTarget.value
  }
  return activeStudentTarget.value
})

const addNewActions = computed(() => {
  const actions = [{ name: '新建学员并关联', value: 'student' }]
  if (!isGroupChat.value) {
    actions.push({ name: '新建线索并关联', value: 'consultation' })
  }
  return actions
})

const moreActions = computed(() => {
  const actions = []
  if (isGroupChat.value) {
    actions.push({ name: '添加学员', value: 'addStudent' })
  }
  actions.push({ name: '解除当前关联', value: 'unbind', color: '#ef4444' })
  return actions
})

const pageTitle = computed(() => {
  if (!bindingInfo.value?.linked) {
    return externalContactName.value || sessionTypeText.value
  }
  if (activeStudentTarget.value) {
    return activeStudentTarget.value.name || '学员详情'
  }
  if (consultationTarget.value) {
    return consultationTarget.value.name || '线索详情'
  }
  return externalContactName.value || sessionTypeText.value
})

watch(pageTitle, (val) => {
  if (val) {
    document.title = val
  }
}, { immediate: true })

onMounted(async () => {
  try {
    const code = route.query?.code
    await ensureWecomLogin(code)
    try {
      await registerWecomApp({
        jsApiList: [
          'getContext',
          'getCurExternalContact',
          'getCurExternalChat'
        ]
      })
    } catch (sdkErr) {
      console.warn('企微JS-SDK注册失败，可能非企微侧边栏环境:', sdkErr)
    }
    let contextInfo = null
    try {
      contextInfo = await getWecomContextInfo()
      // contextInfo = {
      //   type: 'group_chat_tools',
      //   result: {
      //     chatId: 'wrwHH_DAAAHt_CzjsGManmncsmizBayQ',
      //     userId: 'wmwHH_DAAAMud7flujosFBVqyoNKIKHg'
      //   }
      // }
    } catch (ctxErr) {
      console.warn('获取企微上下文失败:', ctxErr)
    }
    if (contextInfo && contextInfo.result && contextInfo.result.userId) {
      sessionType.value = contextInfo.type || 'single_chat_tools'
      sessionId.value = contextInfo.result.userId
    } else if (contextInfo && contextInfo.result && contextInfo.result.chatId) {
      sessionType.value = contextInfo.type || 'group_chat_tools'
      sessionId.value = contextInfo.result.chatId
    } else {
      sessionType.value = route.query?.sessionType || ''
      sessionId.value = route.query?.sessionId || ''
    }

    externalContactName.value = route.query?.userName || ''

    if (!sessionId.value) {
      showToast('无法获取当前会话信息，请在企微侧边栏中打开')
      return
    }
    await loadBinding()
  } catch (error) {
    console.error('CustomerDetail init failed:', error)
    showToast(error?.message || '初始化失败')
  } finally {
    loading.value = false
  }
})

async function loadBinding() {
  try {
    const res = await getCurrentBinding({
      sessionType: sessionType.value,
      sessionId: sessionId.value
    })
    bindingInfo.value = res || { linked: false }
    if (bindingInfo.value.linked) {
      if (studentTargets.value.length) {
        activeStudentBindingId.value = studentTargets.value[0].bindingId
        activeViewType.value = 'student'
      } else if (hasConsultation.value) {
        activeViewType.value = 'consultation'
      }
    }
  } catch (e) {
    console.error('加载绑定信息失败:', e)
    bindingInfo.value = { linked: false }
    showToast(e?.message || '加载绑定信息失败')
  }
}

function handleOpenSearchPopup() {
  searchInitialTab.value = 'student'
  showSearchPopup.value = true
}

function handleAddNew() {
  showAddNewSheet.value = true
}

function handleAddStudent() {
  searchInitialTab.value = 'student'
  showSearchPopup.value = true
}

function handleAddConsultation() {
  searchInitialTab.value = 'consultation'
  showSearchPopup.value = true
}

function onAddNewSelect(action) {
  showAddNewSheet.value = false
  if (action.value === 'student') {
    showStudentCreate.value = true
  } else {
    showConsultationCreate.value = true
  }
}

function onMoreSelect(action) {
  showMoreSheet.value = false
  if (action.value === 'addStudent') {
    handleAddStudent()
  } else if (action.value === 'addConsultation') {
    handleAddConsultation()
  } else if (action.value === 'unbind') {
    handleUnbind(currentViewTarget.value)
  }
}

function handleBound() {
  loadBinding()
}

function switchStudent(bindingId) {
  activeStudentBindingId.value = bindingId
  activeViewType.value = 'student'
}

async function handleUnbind(target) {
  if (!target?.bindingId) return
  try {
    await showConfirmDialog({
      title: '取消关联',
      message: `确定要解除与「${target.name || ''}」的关联吗？`,
      confirmButtonText: '解除',
      confirmButtonColor: '#ef4444'
    })
    await unbindBinding(target.bindingId)
    showToast('已解除关联')
    await loadBinding()
  } catch (e) {
    if (e === 'cancel') return
    showToast(e?.message || '解绑失败')
  }
}

async function handleCreateStudent(payload) {
  try {
    showLoadingToast({ message: '创建中...', forbidClick: true })
    await createStudentBinding(sessionType.value, sessionId.value, payload)
    closeToast()
    showToast('创建并关联成功')
    showStudentCreate.value = false
    await loadBinding()
  } catch (e) {
    closeToast()
    showToast(e?.message || '创建失败')
  }
}

async function handleCreateConsultation(payload) {
  try {
    showLoadingToast({ message: '创建中...', forbidClick: true })
    await createConsultationBinding(sessionType.value, sessionId.value, payload)
    closeToast()
    showToast('创建并关联成功')
    showConsultationCreate.value = false
    await loadBinding()
  } catch (e) {
    closeToast()
    showToast(e?.message || '创建失败')
  }
}
</script>

<style scoped>
.customer-detail-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f5ff 0%, #f7f8fc 120px);
  padding: 16px;
  box-sizing: border-box;
  padding-top: calc(16px + constant(safe-area-inset-top));
  padding-top: calc(16px + env(safe-area-inset-top));
}

.loading-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 12px;
  color: #94a3b8;
}

.loading-text {
  font-size: 13px;
  color: #64748b;
}

.empty-state {
  margin-top: 48px;
  padding: 32px 20px;
  text-align: center;
}

.empty-text {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.empty-text-tip {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  max-width: 280px;
  margin: 0 auto 28px;
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 280px;
  margin: 0 auto;
}

.primary-action-btn,
.secondary-action-btn {
  height: 48px;
  border-radius: 24px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  transition: all 0.2s ease;
}

.primary-action-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
}

.primary-action-btn:active {
  transform: scale(0.98);
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.2);
}

.secondary-action-btn {
  background: #fff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.secondary-action-btn:active {
  background: #eff6ff;
}

.linked-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.more-btn-float {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}

.tabs-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-more-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #fff;
  color: #64748b;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.header-more-btn .van-icon {
  transform: rotate(90deg);
}

.header-more-btn:active {
  background: #f1f5f9;
}

.student-tabs-bar {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.student-tab-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

.student-tab-item.active {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}

.student-tab-close {
  font-size: 13px;
  opacity: 0.7;
}

.consultation-single-bar {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.consultation-single-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 999px;
  background: #2563eb;
  border: 1px solid #2563eb;
  font-size: 13px;
  color: #fff;
  font-weight: 500;
}

.consultation-single-close {
  font-size: 13px;
  opacity: 0.8;
}
</style>
